import { useState, useCallback, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Custom hook for Text-to-Speech with ElevenLabs + Web Speech Synthesis fallback
 * Features:
 * - Primary: ElevenLabs with Indian voices (Raju, Sweetie)
 * - Fallback: Browser Web Speech Synthesis
 * - Auto-callback when speech ends (for auto-listen)
 * - Hinglish-friendly prompting
 */
export const useTTS = (options = {}) => {
  const {
    tutor = 'toby', // 'toby' or 'spark'
    onSpeechStart = () => {},
    onSpeechEnd = () => {},
    voiceEnabled = true
  } = options;

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [useElevenLabs, setUseElevenLabs] = useState(true);
  const [error, setError] = useState(null);
  
  const audioRef = useRef(new Audio());
  const utteranceRef = useRef(null);

  // Web Speech Synthesis voices for fallback
  const getWebSpeechVoice = useCallback((isMale = true) => {
    const voices = window.speechSynthesis.getVoices();
    
    // Try to find Hindi voice first
    let voice = voices.find(v => 
      v.lang.includes('hi') && 
      (isMale ? v.name.toLowerCase().includes('male') : !v.name.toLowerCase().includes('male'))
    );
    
    // Fallback to any Hindi voice
    if (!voice) {
      voice = voices.find(v => v.lang.includes('hi'));
    }
    
    // Fallback to Indian English
    if (!voice) {
      voice = voices.find(v => v.lang.includes('en-IN'));
    }
    
    // Final fallback to default
    return voice || voices[0];
  }, []);

  // Convert formal text to Hinglish conversational style
  const toHinglish = useCallback((text) => {
    // Replace formal greetings with casual ones
    let hinglish = text
      .replace(/Namaste/gi, 'Hi')
      .replace(/Namaskar/gi, 'Hello')
      .replace(/Dhanyavaad/gi, 'Thank you')
      .replace(/Kripya/gi, 'Please')
      .replace(/Aapka swagat hai/gi, 'Welcome')
      .replace(/Bahut achha/gi, 'Bahut achha')
      .replace(/\bAap\b/g, 'Tum') // Formal 'you' to informal
      .replace(/\bAapne\b/g, 'Tumne');
    
    return hinglish;
  }, []);

  // Speak using ElevenLabs API
  const speakWithElevenLabs = useCallback(async (text, emotion = 'neutral') => {
    try {
      const hinglishText = toHinglish(text);
      
      const response = await fetch(`${API}/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: hinglishText,
          tutor: tutor,
          emotion: emotion
        })
      });
      
      const data = await response.json();
      
      if (data.audio_url && !data.mock) {
        return new Promise((resolve, reject) => {
          audioRef.current.src = data.audio_url;
          
          audioRef.current.onplay = () => {
            setIsSpeaking(true);
            onSpeechStart();
          };
          
          audioRef.current.onended = () => {
            setIsSpeaking(false);
            onSpeechEnd();
            resolve(true);
          };
          
          audioRef.current.onerror = (err) => {
            console.error('Audio play error:', err);
            setIsSpeaking(false);
            reject(err);
          };
          
          audioRef.current.play().catch(reject);
        });
      } else {
        // ElevenLabs returned mock/error, use fallback
        throw new Error(data.error || 'ElevenLabs unavailable');
      }
    } catch (err) {
      console.error('ElevenLabs error:', err);
      setUseElevenLabs(false);
      throw err;
    }
  }, [tutor, toHinglish, onSpeechStart, onSpeechEnd]);

  // Speak using Web Speech Synthesis (fallback)
  const speakWithWebSpeech = useCallback((text) => {
    return new Promise((resolve, reject) => {
      if (!('speechSynthesis' in window)) {
        reject(new Error('Web Speech Synthesis not supported'));
        return;
      }

      // Cancel any ongoing speech
      window.speechSynthesis.cancel();

      const hinglishText = toHinglish(text);
      const utterance = new SpeechSynthesisUtterance(hinglishText);
      
      // Set voice based on tutor
      const isMale = tutor === 'toby';
      utterance.voice = getWebSpeechVoice(isMale);
      
      // Adjust settings for tutor personality
      if (tutor === 'spark') {
        utterance.rate = 1.1; // Slightly faster, energetic
        utterance.pitch = 1.2; // Higher pitch, cheerful
      } else {
        utterance.rate = 0.95; // Slightly slower, wise
        utterance.pitch = 0.9; // Deeper, mature
      }
      
      utterance.volume = 1;

      utterance.onstart = () => {
        setIsSpeaking(true);
        onSpeechStart();
      };

      utterance.onend = () => {
        setIsSpeaking(false);
        onSpeechEnd();
        resolve(true);
      };

      utterance.onerror = (event) => {
        console.error('Web Speech error:', event.error);
        setIsSpeaking(false);
        reject(event.error);
      };

      utteranceRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    });
  }, [tutor, toHinglish, getWebSpeechVoice, onSpeechStart, onSpeechEnd]);

  // Main speak function with automatic fallback
  const speak = useCallback(async (text, emotion = 'neutral') => {
    if (!voiceEnabled || !text) return;

    setError(null);

    try {
      // Try ElevenLabs first
      if (useElevenLabs) {
        await speakWithElevenLabs(text, emotion);
        return;
      }
    } catch (err) {
      console.log('ElevenLabs failed, falling back to Web Speech');
    }

    // Fallback to Web Speech Synthesis
    try {
      await speakWithWebSpeech(text);
    } catch (err) {
      console.error('All TTS methods failed:', err);
      setError('Voice unavailable');
      setIsSpeaking(false);
      onSpeechEnd(); // Still trigger end callback
    }
  }, [voiceEnabled, useElevenLabs, speakWithElevenLabs, speakWithWebSpeech, onSpeechEnd]);

  // Stop speaking
  const stopSpeaking = useCallback(() => {
    // Stop ElevenLabs audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    // Stop Web Speech
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }

    setIsSpeaking(false);
  }, []);

  // Reset to try ElevenLabs again
  const resetFallback = useCallback(() => {
    setUseElevenLabs(true);
    setError(null);
  }, []);

  return {
    speak,
    stopSpeaking,
    isSpeaking,
    useElevenLabs,
    error,
    resetFallback,
    audioRef
  };
};

export default useTTS;
